import { Link, useParams } from "react-router-dom";

import { HelpLayout } from "../help/HelpLayout";
import { helpArticleById } from "../help/corpus";
import { renderHelpBody } from "../help/markdown";

/**
 * Статья справки по адресу `/help/:articleId` (E16).
 *
 * АДРЕС ОСМЫСЛЕННЫЙ, А НЕ ПОРЯДКОВЫЙ (план §5, п. 1): по такой ссылке можно послать коллегу,
 * и она переживёт перестановку статей в оглавлении.
 *
 * ВЫХОД ЕСТЬ ИЗ ЛЮБОГО СОСТОЯНИЯ (план §5, п. 8): и из статьи, и из «статья не найдена» —
 * путь в оглавление. Устаревшая ссылка приводит не к пустому экрану, а к объяснению.
 *
 * БЛОКА «РЯДОМ ПО ТЕМЕ» ЗДЕСЬ БОЛЬШЕ НЕТ (доработка по приёмке 22.08.2026). Он был вторым
 * выходом из статьи, пока оглавление жило отдельным экраном; с постоянным деревом слева он стал
 * дословным повтором того, что и так видно рядом — та же тема, те же соседи, ещё и подсвечено,
 * где ты. Повтор на экране не нейтрален: он занимает место и заставляет читать список дважды.
 */

/** Ссылка «наверх» — одна и та же для статьи и для «не найдено». */
function BackToIndex({ className }: { className?: string }) {
  return (
    <Link to="/help" className={`text-[14px] text-accent underline ${className ?? ""}`.trim()}>
      ← Все статьи справки
    </Link>
  );
}

export function HelpArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = articleId === undefined ? null : helpArticleById(articleId);

  if (article === null) {
    return (
      <HelpLayout>
        <section aria-labelledby="help-404-title">
          <BackToIndex />
          <h1 id="help-404-title" className="mt-3 text-[20px] font-bold text-ink">
            Статья не найдена
          </h1>
          <p className="mt-3 max-w-[var(--w-text)] text-[14px] text-ink">
            Такой статьи в справке нет — возможно, ссылка устарела или её переименовали. Откройте
            оглавление: там есть поиск по тексту всех статей.
          </p>
          <Link
            to="/help"
            className="mt-4 inline-block rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface"
          >
            Открыть оглавление справки
          </Link>
        </section>
      </HelpLayout>
    );
  }

  return (
    <HelpLayout>
      <article aria-labelledby="help-article-title">
        <BackToIndex />
        <h1 id="help-article-title" className="mt-3 text-[20px] font-bold text-ink">
          {article.title}
        </h1>
        {/*
          Ширина строки статьи ограничена колонкой текста (--w-text ≈ 70 знаков) — вариант Т2
          решения владельца 07.09.2026, docs/07 §6а. До CR-5 текст шёл во всю ширину панели
          (~135 знаков); с расширением рабочей области до 1600 px та же строка выросла бы
          до ~200 — вдвое выше нормы читаемости и в 2,5 раза выше предела доступности WCAG.
          Освободившееся справа место остаётся пустым намеренно.
        */}
        <div className="mt-4 max-w-[var(--w-text)]">{renderHelpBody(article.body)}</div>
      </article>
    </HelpLayout>
  );
}
